// Keeps the last few errors the app hit so a bug report can carry them.
// Installed once from main.tsx. Records window errors, unhandled promise
// rejections and console.error calls into a small ring buffer; nothing leaves
// the browser until the user submits a bug report.

import type { BugContext, BugContextError } from '../../../supabase/functions/_shared/bug-brief';
import { isPartnrV2Enabled } from '@/lib/partnr/flag';

const MAX_ENTRIES = 20;
const MAX_MESSAGE = 500;
const MAX_STACK = 2000;

const buffer: BugContextError[] = [];
let installed = false;

function stringify(value: unknown): string {
  if (value instanceof Error) return `${value.name}: ${value.message}`;
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

export function recordError(kind: BugContextError['kind'], message: string, stack?: string) {
  buffer.push({
    at: new Date().toISOString(),
    kind,
    message: message.slice(0, MAX_MESSAGE),
    ...(stack ? { stack: stack.slice(0, MAX_STACK) } : {}),
  });
  if (buffer.length > MAX_ENTRIES) buffer.splice(0, buffer.length - MAX_ENTRIES);
}

export function getRecentErrors(): BugContextError[] {
  return buffer.slice();
}

/** Test hook. */
export function clearRecentErrors() {
  buffer.length = 0;
}

export function installErrorBuffer() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (event) => {
    const err = event.error instanceof Error ? event.error : null;
    recordError('error', err ? stringify(err) : event.message || 'Unknown error', err?.stack);
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    recordError('rejection', stringify(reason), reason instanceof Error ? reason.stack : undefined);
  });

  const original = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    try {
      const err = args.find((a): a is Error => a instanceof Error);
      recordError('console', args.map(stringify).join(' '), err?.stack);
    } catch {
      // Never let the buffer break logging
    }
    original(...args);
  };
}

/** Everything a bug report should carry about where the user was. */
export function collectBugContext(): BugContext {
  let partnr: string | undefined;
  try {
    partnr = isPartnrV2Enabled() ? 'v2' : 'v1';
  } catch {
    partnr = undefined;
  }
  let timezone: string | undefined;
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    timezone = undefined;
  }
  return {
    route: `${window.location.pathname}${window.location.search}`,
    build: `${typeof __BUILD_SHA__ !== 'undefined' ? __BUILD_SHA__ : 'unknown'} (${import.meta.env.MODE})`,
    viewport: { w: window.innerWidth, h: window.innerHeight, dpr: window.devicePixelRatio || 1 },
    timezone,
    partnr,
    online: navigator.onLine,
    errors: getRecentErrors(),
  };
}
